import React, { UIEvent, useCallback, useEffect, useMemo, useState } from 'react';

import {
  Button,
  Checkbox,
  createStyles,
  Divider,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { SelectItem } from '@mantine/core/lib/Select/types';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  IconCheck,
  IconExclamationCircle,
  IconInfoSquareRounded,
  IconSearch,
  IconTag,
} from '@tabler/icons-react';
import Drawer from 'components/Drawer';
import EventTypeMultiSelectAutoComplete, {
  IEventTypeMultiSelectAutoComplete,
} from 'components/form-fields/auto-complete-fields/EventTypeMultiSelectAutoComplete';
import PaymentMethodSelect from 'components/form-fields/auto-complete-fields/PaymentMethodSelect';
import AthleteStatusSelect, {
  athleteStatusOptions,
} from 'components/form-fields/select-fields/AthleteStatusSelect';
import DiscountsSelect from 'components/form-fields/select-fields/DiscountsSelect';
import PackageSelect from 'components/form-fields/select-fields/PackageSelect';
import KiloActionIcon from 'components/KiloActionIcon';
import KiloTooltip, { TOOLTIP_MESSAGES } from 'components/KiloTooltip';
import PackageTypeLabel from 'components/PackageTypeLabel';
import TapToPayModal from 'components/ShoppingCart/components/TapToPayModal';
import StripeTerminalReaderStatusIndicator from 'components/StripeTerminalReader/StripeTerminalReaderStatusIndicator';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useCreateAthletePackage from 'hooks/athletePackage/useCreateAthletePackage';
import useGym from 'hooks/gym/useGym';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import usePackage from 'hooks/package/usePackage';
import usePackages from 'hooks/package/usePackages';
import useStripeTerminalReaderStatus from 'hooks/stripeTerminalReader/useStripeTerminalReaderStatus';
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableInstance,
  useMantineReactTable,
} from 'mantine-react-table';
import { buildRenewalSummaryFromRules } from 'pages/private/owner/Package/components/PackageDrawer/components/PackageForm/components/renewalSummaryText';
import { useNavigate } from 'react-router-dom';
import {
  AthletePackage,
  BillingPlanWithInstallments,
  EligibleDiscountList,
  Installment,
  PackageListView,
} from 'types';
import { NonStripePaymentMethod, TAP_TO_PAY_VALUE } from 'types/enums';
import {
  computeSessionValue,
  computeTotalEstimatedSessions,
  getAttendanceRuleSummary,
} from 'utils/billing.helpers';
import { parseDateTimeToDate } from 'utils/date-functions';
import { computeDiscountedPrice, formatDiscountValue } from 'utils/discount.helpers';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

const formId = 'create-athlete-package-form';

interface FormValues {
  athletePackageId: string;
  package: PackageListView | null;
  billingPlan: BillingPlanWithInstallments | null;
  athleteStatusOnStart: Exclude<AthletePackage['athleteStatusOnStart'], null> | 'No status change';
  athleteStatusOnEnd: Exclude<AthletePackage['athleteStatusOnEnd'], null> | 'No status change';
  startDate: Date;
  billingStartDate: Date;
  autoRenew: string;
  autoPay: string;
  paymentMethod: string;
  nonStripePaymentMethod: 'Cash or Check' | 'Gift Card' | 'E Transfer' | 'POS Terminal' | null;
  isTapToPay: boolean;
  isProrated: boolean;
  includeFollowUpPackage: boolean;
  successorPackage: string | null;
  successorBillingPlan: string | null;
  successorStartDate: Date | null;
  successorBillingStartDate: Date | null;
  successorStatusOnStart: string | null;
  successorStatusOnEnd: string | null;
  successorIsProrated: boolean | null;
  discount: EligibleDiscountList | null;
  applyDiscountToAll: boolean;
}

export interface CapturedChangeConfig {
  // Holding the rich PackageListView / BillingPlanWithInstallments objects (rather than
  // only ids) lets us rehydrate the form fully when the drawer is reopened after Back.
  package: PackageListView;
  billingPlan: BillingPlanWithInstallments;
  billingStartDate: Date;
  athleteStatusOnStart: string | null;
  athleteStatusOnEnd: string | null;
  paymentMethodId: string | null;
  nonStripePaymentMethod: FormValues['nonStripePaymentMethod'];
  autoPay: boolean;
  autoRenew: boolean;
  isProrated: boolean;
  followUpPackageId: string | null;
  followUpBillingPlanId: string | null;
  discount: EligibleDiscountList | null;
  applyDiscountToAll: boolean;
}

interface IAthletePackageCreateFormProps {
  title: JSX.Element;
  opened: boolean;
  closeHandler: () => void;
  athleteId?: string;
  // When mode='capture', the form does NOT call the create endpoint on submit.
  // Instead, onCapture is invoked with the form values shaped as CapturedChangeConfig,
  // and the drawer is closed by the caller. Used by the Change Package flow.
  mode?: 'create' | 'capture';
  onCapture?: (captured: CapturedChangeConfig) => void;
  lockedStartDate?: Date;
  submitButtonLabel?: string;
  initialCaptured?: CapturedChangeConfig | null;
  // Capture mode only: the auto-pay setting of the package being changed. The new package
  // starts from it instead of from off, so losing auto-pay through a change has to be a
  // deliberate act rather than a field nobody looked at.
  inheritedAutoPay?: boolean;
}

const useStyles = createStyles(theme => ({
  select: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: '1fr',
      width: '16rem',
    },
  },
  detailsContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    flexGrow: 1,
    gap: theme.spacing.md,
    [theme.fn.smallerThan('sm')]: {
      display: 'block',
    },
  },
  detailsInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm,
    width: '50%',
    [theme.fn.smallerThan('sm')]: {
      width: '100%',
    },
  },
  selectInDrawer: {
    display: 'grid',
    gridTemplateColumns: '7fr 6fr',
    gap: theme.spacing.xs,
    marginBottom: '10px',
  },
  paper: {
    width: '50%',
    [theme.fn.smallerThan('sm')]: {
      width: '100%',
      marginTop: theme.spacing.xs,
    },
  },
}));
const AthletePackageCreateForm = ({
  title,
  opened,
  closeHandler,
  athleteId,
  mode = 'create',
  onCapture,
  lockedStartDate,
  submitButtonLabel,
  initialCaptured,
  inheritedAutoPay,
}: IAthletePackageCreateFormProps): JSX.Element => {
  const isCaptureMode = mode === 'capture';
  // The value the auto-pay field resets to: inherited in the Change flow, off everywhere else.
  const defaultAutoPay = isCaptureMode && inheritedAutoPay ? 'True' : 'False';
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);

  const { formatCurrency, copy, isTaxInclusive } = useLocalization();
  const { session } = useAuth();
  const navigate = useNavigate();

  const staffTracking = useStaffTracking();
  const createMutation = useCreateAthletePackage();

  const { data: gymData } = useGym(session?.gym?.gymId);
  const { data: stripeTerminalReaderStatusData } = useStripeTerminalReaderStatus({
    enabled: !!gymData?.data.stripeTerminalReader,
  });

  const [isTapToPayModalOpened, setIsTapToPayModalOpened] = useState(false);
  const [tapToPayInvoiceId, setTapToPayInvoiceId] = useState<string | null>(null);
  const [tapToPayIsChargeNow, setTapToPayIsChargeNow] = useState(true);

  const [search, setSearch] = useState<string>('');
  const [eventTypes, setEventTypes] = useState<IEventTypeMultiSelectAutoComplete[]>([]);
  const [packagesTableData, setPackagesTableData] = useState<PackageListView[]>([]);
  const [billingPlanTableData, setBillingPlanTableData] = useState<
    (BillingPlanWithInstallments & { sessionValue: string })[]
  >([]);
  const [billingPlanOptions, setBillingPlanOptions] = useState<SelectItem[]>([]);

  const [isSoldOutModalOpen, { open: openSoldOutModal, close: closeSoldOutModal }] = useDisclosure(false);
  const [isPackageDrawerOpen, { open: openPackageDrawer, close: closePackageDrawer }] = useDisclosure(false);
  const [isBillingDrawerOpen, { open: openBillingDrawer, close: closeBillingDrawer }] = useDisclosure(false);

  const {
    data: packagesData,
    isLoading: packagesIsLoading,
    isError: packagesIsError,
    isFetching: packagesIsFetching,
    fetchNextPage: packagesFetchNextPage,
  } = usePackages({
    search,
    eventTypes: eventTypes.map(v => v.eventTypeId),
    cacheKey: 'drawer-athlete-package',
    athleteId,
  });

  const totalPackagesCount = packagesData?.pages?.[0]?.data?.count ?? 0;
  const totalPackagesFetchedCount = packagesTableData.length;

  const validateAthleteEndStatus = (values: FormValues): string | null => {
    if (
      values.athleteStatusOnStart !== 'No status change' &&
      values.athleteStatusOnEnd !== 'No status change' &&
      values.athleteStatusOnStart === values.athleteStatusOnEnd
    ) {
      return 'Cannot select the same status as start';
    }
    return null;
  };

  const lockedDate = lockedStartDate ?? new Date();
  const form = useForm<FormValues>({
    initialValues: {
      athletePackageId: '',
      package: null,
      billingPlan: null,
      athleteStatusOnStart: 'No status change',
      athleteStatusOnEnd: 'No status change',
      startDate: lockedStartDate ?? new Date(),
      billingStartDate: lockedStartDate ?? new Date(),
      autoRenew: 'False',
      autoPay: defaultAutoPay,
      paymentMethod: '',
      nonStripePaymentMethod: null,
      isTapToPay: false,
      isProrated: false,
      includeFollowUpPackage: false,
      successorPackage: '',
      successorBillingPlan: '',
      successorStartDate: lockedDate,
      successorBillingStartDate: lockedDate,
      successorStatusOnStart: 'No status change',
      successorStatusOnEnd: 'No status change',
      successorIsProrated: false,
      discount: null,
      applyDiscountToAll: false,
    },
    validate: {
      package: value => (!value ? 'Package must be selected' : null),
      billingPlan: value => (!value ? 'Billing plan must be selected' : null),
      athleteStatusOnStart: value => {
        return athleteStatusOptions.onStart.includes(value) ? null : 'Please select a valid status';
      },
      athleteStatusOnEnd: (value, values) => {
        if (!athleteStatusOptions.onEnd.includes(value)) {
          return 'Please select a valid status';
        }
        return validateAthleteEndStatus(values);
      },
      startDate: value => (!value ? 'Package start date must be defined' : null),
      billingStartDate: value => (!value ? 'Billing start date must be defined' : null),
      autoPay: (value, values) => {
        return value === 'True' && values.nonStripePaymentMethod !== null
          ? 'Autopay cannot be on when payment method is a non-stripe type'
          : null;
      },
      paymentMethod: (value, values) => {
        return values.nonStripePaymentMethod === null && !value ? 'Payment method must be selected' : null;
      },
      successorPackage: (value, values) => {
        if (!values.includeFollowUpPackage) {
          return null;
        }
        return value && value.length > 0 ? null : 'Successor package must be selected';
      },
      successorBillingPlan: (value, values) => {
        if (!values.includeFollowUpPackage) {
          return null;
        }
        return value && value.length > 0 ? null : 'Successor billing plan must be selected';
      },
    },
  });

  const {
    data: packageData,
    isLoading: packageIsLoading,
    isSuccess: packageIsSuccess,
  } = usePackage(form.values.package?.packageId);

  const {
    data: successorPackageData,
    isSuccess: isSuccessSuccessorPackage,
    isLoading: isLoadingSuccessorPackage,
  } = usePackage(form.values.successorPackage || '');

  const isCreditBasedPackage = form.values.package?.type === 'CREDIT_BASED';
  const allowsFollowUpPackage = form.values.package?.type === 'TIME_BASED';

  const setDefaultSuccessorDates = (startDate: Date): void => {
    if (packageData?.data && packageIsSuccess) {
      const attendanceRules = packageData?.data.attendanceRules;

      let maxEndDate = dayjs(startDate);

      attendanceRules?.forEach(ar => {
        let ruleEndDate = dayjs(startDate);

        if (ar.delayInterval && ar.delayIntervalCount) {
          ruleEndDate = ruleEndDate.add(ar.delayIntervalCount || 0, ar.delayInterval || 'day');
        }

        if (ar.validityInterval && ar.validityIntervalCount) {
          ruleEndDate = ruleEndDate.add(ar.validityIntervalCount || 0, ar.validityInterval || 'day');
        }

        if (ruleEndDate.isAfter(maxEndDate)) {
          maxEndDate = ruleEndDate;
        }
      });

      const updatedDate = maxEndDate.toDate();
      form.setFieldValue('successorStartDate', updatedDate);
      form.setFieldValue('successorBillingStartDate', updatedDate);
    }
  };

  const handleSelectPackage = (
    table: MRT_TableInstance<PackageListView>,
    row: MRT_Row<PackageListView>,
  ): void => {
    table.resetExpanded();
    row.toggleExpanded();
    form.setFieldValue('billingPlan', null);
    form.setFieldValue('package', row.original);
    form.setFieldValue('athleteStatusOnStart', row.original.athleteStatusOnStart || 'No status change');
    form.setFieldValue('athleteStatusOnEnd', row.original.athleteStatusOnEnd || 'No status change');
    const autoRenew = row.original.autoRenew && !row.original.oneTimePurchase;
    form.setFieldValue('autoRenew', autoRenew ? 'True' : 'False');
    if (lockedStartDate) {
      form.setFieldValue('startDate', lockedStartDate);
      form.setFieldValue('billingStartDate', lockedStartDate);
    } else if (row.original.startDate) {
      const startDate = parseDateTimeToDate(row.original.startDate, session?.gym?.timezone);
      form.setFieldValue('startDate', startDate);
      form.setFieldValue('billingStartDate', startDate);
    } else {
      const newDate = new Date();
      form.setFieldValue('startDate', newDate);
      form.setFieldValue('billingStartDate', newDate);
    }
    // The inherited value counts alongside the destination package's autoRenew. Without it,
    // picking a destination that does not auto-renew wiped the seed, and the Change flow only
    // got it back because choosing a payment method resets the field to its default afterwards.
    // inheritedAutoPay is undefined outside capture mode, so the create drawer is unaffected.
    form.setFieldValue(
      'autoPay',
      form.values.nonStripePaymentMethod === null && (row.original.autoRenew || inheritedAutoPay)
        ? 'True'
        : 'False',
    );
    form.setFieldValue('includeFollowUpPackage', false);
    form.setFieldValue('successorPackage', row.original.successorPackage || '');
    form.setFieldValue('successorBillingPlan', row.original.successorBillingPlan || '');
  };

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (
          scrollHeight - scrollTop - clientHeight < 10 &&
          !packagesIsFetching &&
          totalPackagesFetchedCount < totalPackagesCount
        ) {
          packagesFetchNextPage();
        }
      }
    },
    [packagesFetchNextPage, packagesIsFetching, totalPackagesCount, totalPackagesFetchedCount],
  );

  const renderPackageDetailPanel = (row: MRT_Row<PackageListView>): JSX.Element => {
    if (row.getIsExpanded()) {
      return (
        <>
          {packageIsLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Loader />
            </div>
          )}
          {packageIsSuccess &&
            packageData?.data &&
            getAttendanceRuleSummary(packageData.data.attendanceRules).map(rule => (
              <Group grow align='top' key={rule.key} pl='52px'>
                <Text>{rule.name}</Text>
                <Text>{rule.summary}</Text>
              </Group>
            ))}
          {packageIsSuccess && packageData?.data?.type === 'TIME_OR_CREDIT_BASED' && (
            <Text pl='52px' data-cy='packageRenewalSummary'>
              {buildRenewalSummaryFromRules(
                packageData.data.attendanceRules,
                form.values.autoRenew === 'True' && !packageData.data.oneTimePurchase,
              )}
            </Text>
          )}
        </>
      );
    }
    return <></>;
  };

  const packageColumns = useMemo<MRT_ColumnDef<PackageListView>[]>(
    () => [
      {
        header: 'name',
        Header: (
          <Text color='dimmed' weight='bold'>
            Package Name
          </Text>
        ),
        Cell: ({ cell }) => (
          <Stack spacing='0'>
            <Text data-cy='athletePackageRow' weight='bold'>
              {cell.row.original.name}
            </Text>
            {cell.row.getIsExpanded() && <PackageTypeLabel type={cell.row.original.type} fs='italic' />}
          </Stack>
        ),
        size: 400,
      },
    ],
    [],
  );

  const packagesTable = useMantineReactTable({
    columns: packageColumns,
    data: packagesTableData,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableExpanding: true,
    enableExpandAll: false,
    enableFilters: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableMultiRowSelection: false,
    enablePagination: false,
    enableRowSelection: false,
    enableSorting: false,
    enableStickyHeader: true,
    enableTableFooter: false,
    enableTopToolbar: false,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    mantineTableContainerProps: {
      sx: { maxHeight: '250px' },
      onScroll: (event: UIEvent<HTMLDivElement>) => fetchMoreOnBottomReached(event.target as HTMLDivElement),
    },
    mantineTableBodyRowProps: ({ row, table: t }) => ({
      onClick: () => handleSelectPackage(t, row),
      'data-cy': 'athletePackageRow',
      sx: {
        cursor: 'pointer',
        backgroundColor:
          form.values.package?.packageId === row.original.packageId ? theme.colors.blue[0] : undefined,
        ':hover': {
          backgroundColor: `${
            form.values.package?.packageId === row.original.packageId
              ? theme.colors.blue[0]
              : theme.colors.gray[2]
          } !important`,
        },
        '& td:hover': {
          backgroundColor: 'inherit !important',
        },
        '& td': {
          backgroundColor: 'inherit !important',
        },
      },
    }),
    mantineExpandButtonProps: ({ row, table: t }) => ({
      onClick: () => handleSelectPackage(t, row),
    }),
    mantineBottomToolbarProps: {
      sx: {
        height: '0px !important',
        minHeight: '0px !important',
      },
    },
    mantineToolbarAlertBannerProps: {
      color: 'red',
      children: 'Error loading data',
    },
    displayColumnDefOptions: { 'mrt-row-expand': { size: 10, Header: '' } },
    renderDetailPanel: ({ row }) => renderPackageDetailPanel(row),
    renderEmptyRowsFallback: () => (
      <Text size='sm' ta='center'>
        No packages found
      </Text>
    ),
    icons: {
      IconChevronDown: (props: any) => {
        if (props.style.transform === 'rotate(-180deg)') {
          return <IconCheck color={theme.colors.green[5]} size={theme.fontSizes.xl} />;
        }
        return <></>;
      },
    },
    getRowId: row => row.packageId,
    initialState: { density: 'xs' },
    state: {
      isLoading: packagesIsLoading,
      showProgressBars: packagesIsFetching,
      showAlertBanner: packagesIsError,
    },
  });

  const billingPlanColumns = useMemo<
    MRT_ColumnDef<BillingPlanWithInstallments & { sessionValue: string }>[]
  >(
    () => [
      {
        accessorKey: 'name',
        header: 'Billing Plan',
        Header: (
          <Text color='dimmed' weight='bold'>
            Billing Plan
          </Text>
        ),
        Cell: ({ cell }) => (
          <Text data-cy='athletePackageBPRow' weight='bold'>
            {cell.row.original.name}
          </Text>
        ),
        size: isDesktop ? 120 : 80,
      },
      {
        header: 'Total Price',
        Header: (
          <Text color='dimmed' weight='bold'>
            Total Price
          </Text>
        ),
        Cell: ({ cell }) => <Text>{formatCurrency((cell.row.original.totalPrice || 0) / 100)}</Text>,
        size: isDesktop ? 120 : 60,
      },
      {
        accessorKey: 'numberOfInstallments',
        header: 'Installments',
        Header: (
          <Text color='dimmed' weight='bold'>
            Installments
          </Text>
        ),
        size: isDesktop ? 70 : 40,
      },
      {
        accessorKey: 'sessionValue',
        header: 'Session Value',
        Header: (
          <Text color='dimmed' weight='bold'>
            Session Value
          </Text>
        ),
        size: isDesktop ? 70 : 40,
      },
    ],
    [formatCurrency, isDesktop],
  );

  const billingPlanTable = (
    <MantineReactTable
      enableRowSelection={false}
      enableStickyHeader
      enableTopToolbar={false}
      enableBottomToolbar={false}
      enableColumnActions={false}
      enableMultiRowSelection={false}
      enablePagination={false}
      enableSorting={false}
      initialState={{ density: 'xs' }}
      columns={billingPlanColumns}
      data={billingPlanTableData}
      getRowId={row => row.billingPlanId}
      renderEmptyRowsFallback={() => (
        <Text size='sm' ta='center'>
          {form.values.package?.packageId ? 'No billing plans' : 'Select a package'}
        </Text>
      )}
      mantineTableBodyRowProps={({ row }) => ({
        onClick: () => {
          form.setFieldValue('billingPlan', row.original);
        },
        'data-cy': 'athletePackageBillingPlanRow',
        sx: {
          cursor: 'pointer',
          backgroundColor:
            form.values.billingPlan?.billingPlanId === row.original.billingPlanId
              ? theme.colors.blue[0]
              : undefined,
          ':hover': {
            backgroundColor: `${
              form.values.billingPlan?.billingPlanId === row.original.billingPlanId
                ? theme.colors.blue[0]
                : theme.colors.gray[2]
            } !important`,
          },
          '& td:hover': {
            backgroundColor: 'inherit !important',
          },
          '& td': {
            backgroundColor: 'inherit !important',
          },
        },
      })}
    />
  );

  const renderBillingPlanPaymentDatesTable = (
    billingStartDate: Date,
    billingPlan: BillingPlanWithInstallments,
    isProrated: boolean,
    invoiceTrigger: 'immediately' | 'at_package_start' | null = null,
    discount: EligibleDiscountList | null = null,
    applyToAll = false,
  ): JSX.Element => {
    const { installmentInterval, installmentIntervalCount, installments } = billingPlan;
    installments.sort((a: Installment, b: Installment) =>
      a.installmentNumber > b.installmentNumber ? 1 : -1,
    );

    const today = dayjs(new Date());
    const firstIsStillAhead = invoiceTrigger === 'immediately' && today.isBefore(dayjs(billingStartDate));
    let date = dayjs(billingStartDate);
    const data = installments.map(i => {
      const val = {
        ...i,
        date: date.format('MM-DD-YYYY'),
        description: `Installment ${i.installmentNumber}`,
        proratedRatio: 1,
      };
      if (i.installmentNumber === 1 && isProrated) {
        val.description = 'Pro-rated Installment 1';
        const daysInMonth = dayjs(billingStartDate).daysInMonth();
        const numDaysLeft = daysInMonth - dayjs(billingStartDate).date();
        val.proratedRatio = (numDaysLeft + 1) / daysInMonth;
        date = date.date(1);
      }
      if (i.installmentNumber === 1 && firstIsStillAhead) {
        val.date = today.format('MM-DD-YYYY');
      }
      date = date.add(installmentIntervalCount || 0, installmentInterval || 'day');
      return val;
    });

    const getInstallmentBreakdown = (
      ins: (typeof data)[number],
    ): { baseCents: number; finalCents: number; isDiscounted: boolean } => {
      const baseCents = ins.price * ins.proratedRatio;
      const shouldApplyDiscount = !!discount && (applyToAll || ins.installmentNumber === 1);
      const finalCents = shouldApplyDiscount ? computeDiscountedPrice(baseCents, discount) : baseCents;
      return { baseCents, finalCents, isDiscounted: shouldApplyDiscount && finalCents !== baseCents };
    };

    return (
      <Stack spacing='xs'>
        {data.map(ins => {
          const { baseCents, finalCents, isDiscounted } = getInstallmentBreakdown(ins);
          return (
            <React.Fragment key={ins.installmentId}>
              <Divider my={0} />
              <Group grow ta='center'>
                <Text size='sm' data-cy='installmentDate'>
                  {ins.date}
                </Text>
                <Text size='sm' data-cy='installmentNumber'>
                  {ins.description}
                </Text>
                <Stack spacing={0} align='center'>
                  {isDiscounted && (
                    <Text size='sm' color='dimmed' strikethrough>
                      {formatCurrency(baseCents / 100)}
                    </Text>
                  )}
                  <Text size='sm' color={isDiscounted ? 'green' : undefined} data-cy='installmentCost'>
                    {formatCurrency(finalCents / 100)}
                  </Text>
                </Stack>
              </Group>
            </React.Fragment>
          );
        })}
      </Stack>
    );
  };

  const handleEventTypeChange = (values: IEventTypeMultiSelectAutoComplete[]): void => {
    setEventTypes(values);
    form.setFieldValue('billingPlan', null);
    form.setFieldValue('package', null);
    setBillingPlanTableData([]);
    packagesTable.resetExpanded();
  };

  const handleProrationChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    form.setFieldValue('isProrated', e.currentTarget.checked);
    if (e.currentTarget.checked) {
      form.setFieldValue('billingStartDate', form.values.startDate);
    }
  };

  const handleSuccessorProrationChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    form.setFieldValue('successorIsProrated', e.currentTarget.checked);
    if (e.currentTarget.checked) {
      form.setFieldValue('successorBillingStartDate', form.values.successorStartDate);
    }
  };

  const handleStartDateChange = (date: Date): void => {
    form.setFieldValue('startDate', date);
    if (form.values.isProrated || isCreditBasedPackage) {
      form.setFieldValue('billingStartDate', date);
    }
    setDefaultSuccessorDates(date);
  };

  const handleSuccessorStartDateChange = (date: Date): void => {
    form.setFieldValue('successorStartDate', date);
    if (form.values.successorIsProrated) {
      form.setFieldValue('successorBillingStartDate', date);
    }
  };

  const handleIncludeFollowUpChange = (isFollowUpChecked: boolean): void => {
    form.setFieldValue('includeFollowUpPackage', isFollowUpChecked);
    if (isFollowUpChecked) {
      form.setFieldValue('autoRenew', 'False');
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.currentTarget?.value || '';
    setSearch(value);
    form.setFieldValue('billingPlan', null);
    form.setFieldValue('package', null);
    setBillingPlanTableData([]);
    packagesTable.resetExpanded();
  };

  const handleCloseAndReset = (): void => {
    closeHandler();
    form.reset();
    setSearch('');
    setEventTypes([]);
    setBillingPlanTableData([]);
    packagesTable.resetExpanded();
  };

  const handlePaymentChange = (value: string): void => {
    form.setFieldValue('paymentMethod', value);
    if (value === TAP_TO_PAY_VALUE) {
      form.setFieldValue('isTapToPay', true);
      form.setFieldValue('nonStripePaymentMethod', null);
      // Per spec: multi-installment Tap to Pay forces autoPay ON; we lock it on
      // for any tap-to-pay package since the card is always saved.
      form.setFieldValue('autoPay', 'True');
      const numberOfInstallments = form.values.billingPlan?.numberOfInstallments;
      staffTracking.trackTapToPaySelected({
        page: 'athlete_package',
        is_charge_now: !dayjs(form.values.billingStartDate).isAfter(dayjs(), 'day'),
        reader_online: stripeTerminalReaderStatusData?.data.isOnline ?? false,
        package_id: form.values.package?.packageId,
        billing_plan_type: numberOfInstallments
          ? numberOfInstallments > 1
            ? 'multi_installment'
            : 'single'
          : undefined,
        installment_count: numberOfInstallments,
        auto_pay_enabled: true,
      });
    } else if (Object.values(NonStripePaymentMethod).includes(value as NonStripePaymentMethod)) {
      form.setFieldValue('isTapToPay', false);
      form.setFieldValue('nonStripePaymentMethod', value as FormValues['nonStripePaymentMethod']);
      form.setFieldValue('autoPay', 'False');
    } else {
      // Only a selection that forced the field decides it again: cash and check forces auto-pay
      // off, Tap to Pay forces it on. Leaving one of those returns the field to its default,
      // which in the Change flow is the package being changed, since choosing a card is not
      // staff opting out of auto-pay. Card to card keeps whatever staff set, so neither a card
      // swap nor the default-card auto-select that runs when the drawer re-opens can overwrite
      // a value they chose.
      const wasForced = form.values.isTapToPay || form.values.nonStripePaymentMethod !== null;
      form.setFieldValue('isTapToPay', false);
      form.setFieldValue('nonStripePaymentMethod', null);
      if (wasForced) {
        form.setFieldValue('autoPay', defaultAutoPay);
      }
    }
  };

  const handleSuccessorPackageChange = (value: string): void => {
    form.setFieldValue('successorPackage', value);
    if (value) {
      form.setFieldValue('successorBillingPlan', '');
    }
  };

  const handleCapture = (values: FormValues): void => {
    if (!values.package || !values.billingPlan) return;
    const captured: CapturedChangeConfig = {
      package: values.package,
      billingPlan: values.billingPlan,
      billingStartDate: values.billingStartDate,
      athleteStatusOnStart:
        values.athleteStatusOnStart === 'No status change' ? null : values.athleteStatusOnStart,
      athleteStatusOnEnd:
        values.athleteStatusOnEnd === 'No status change' ? null : values.athleteStatusOnEnd,
      paymentMethodId:
        values.isTapToPay || values.nonStripePaymentMethod ? null : values.paymentMethod || null,
      nonStripePaymentMethod: values.nonStripePaymentMethod,
      autoPay: values.autoPay === 'True',
      autoRenew: values.autoRenew === 'True' && !values.package?.oneTimePurchase,
      isProrated: values.isProrated,
      followUpPackageId:
        allowsFollowUpPackage && values.includeFollowUpPackage ? values.successorPackage : null,
      followUpBillingPlanId:
        allowsFollowUpPackage && values.includeFollowUpPackage ? values.successorBillingPlan : null,
      discount: values.discount,
      applyDiscountToAll: values.applyDiscountToAll,
    };
    // Do NOT call closeHandler here. The parent's onCapture handler is expected to flip
    // the drawer's opened prop (e.g., by advancing the wizard phase), which closes the
    // drawer. Calling closeHandler() would be interpreted as a "cancel" by the parent and
    // would reset state.
    onCapture?.(captured);
  };

  const handleSubmit = (values: FormValues): void => {
    const { errorNotification, successNotification } = notificationHandler();

    const valid = form.validate();
    if (valid.hasErrors) {
      return;
    }

    if (isCaptureMode) {
      handleCapture(values);
      return;
    }

    // otherwise the modal would just spin forever waiting for a tap.
    if (values.isTapToPay && !stripeTerminalReaderStatusData?.data.isOnline) {
      errorNotification(
        'Terminal reader offline',
        'The card reader is not online. Please check the device and try again.',
      );
      return;
    }

    if (athleteId && values.billingPlan && values.package) {
      const payload = {
        athletePackageId: uuidv4(),
        athlete: athleteId,
        package: values.package.packageId,
        athleteStatusOnStart:
          values.athleteStatusOnStart === 'No status change' ? null : values.athleteStatusOnStart,
        athleteStatusOnEnd:
          values.athleteStatusOnEnd === 'No status change' ? null : values.athleteStatusOnEnd,
        billingPlan: values.billingPlan.billingPlanId,
        startDate: dayjs(values.startDate).format('YYYY-MM-DD'),
        billingStartDate: dayjs(values.billingStartDate).format('YYYY-MM-DD'),
        autoRenew: values.autoRenew === 'True' && !values.package?.oneTimePurchase,
        paymentMethod: values.isTapToPay || values.nonStripePaymentMethod ? null : values.paymentMethod,
        nonStripePaymentMethod: values.isTapToPay ? null : values.nonStripePaymentMethod,
        autoPay: values.autoPay === 'True',
        isTapToPay: values.isTapToPay,
        isProrated: values.isProrated,

        // Time-based only include package
        successorPackage:
          allowsFollowUpPackage && form.values.includeFollowUpPackage ? values.successorPackage : null,
        successorBillingPlan:
          allowsFollowUpPackage && form.values.includeFollowUpPackage ? values.successorBillingPlan : null,
        successorStartDate:
          allowsFollowUpPackage && form.values.includeFollowUpPackage
            ? dayjs(values.successorStartDate).format('YYYY-MM-DD')
            : null,
        successorBillingStartDate:
          allowsFollowUpPackage && form.values.includeFollowUpPackage
            ? dayjs(values.successorBillingStartDate).format('YYYY-MM-DD')
            : null,
        successorStatusOnStart:
          !isCreditBasedPackage && values.successorStatusOnStart === 'No status change'
            ? null
            : values.successorStatusOnStart,
        successorStatusOnEnd:
          !isCreditBasedPackage && values.successorStatusOnEnd === 'No status change'
            ? null
            : values.successorStatusOnEnd,
        successorIsProrated:
          allowsFollowUpPackage && form.values.includeFollowUpPackage ? values.successorIsProrated : null,
        discount: form.values.discount?.discountId ?? null,
        applyToAll: form.values.applyDiscountToAll,
      };

      createMutation.mutate(payload, {
        onError: error => {
          closeSoldOutModal();
          errorNotification('Athlete package creation failed', error.response?.data.message);
        },
        onSuccess: res => {
          closeSoldOutModal();

          if (values.package?.packageId && values.package?.name) {
            staffTracking.trackAthletePackageCreated(
              athleteId,
              values.package.packageId,
              values.package.name,
              values.package.type,
              values.autoRenew === 'True',
              values.athleteStatusOnStart,
              values.athleteStatusOnEnd,
            );
          }

          if (values.isTapToPay) {
            const isChargeNow = !dayjs(values.billingStartDate).isAfter(dayjs(), 'day');
            const firstInvoiceId = res.data.firstInvoiceId ?? null;
            setTapToPayIsChargeNow(isChargeNow);
            setTapToPayInvoiceId(firstInvoiceId);
            setIsTapToPayModalOpened(true);

            // Card is always saved on Package Assignment tap-to-pay (saved card
            // is needed for future installments / auto-pay).
            if (firstInvoiceId && athleteId) {
              if (isChargeNow) {
                // First installment price drives the amount of the immediate charge.
                const firstInstallmentPrice = values.billingPlan?.installments?.[0]?.price ?? 0;
                staffTracking.trackTapToPayCollectionStarted({
                  page: 'athlete_package',
                  invoice_id: firstInvoiceId,
                  athlete_id: athleteId,
                  amount: firstInstallmentPrice,
                  item_count: 1,
                  save_card: true,
                });
              } else {
                staffTracking.trackTapToPayCardSaveStarted({
                  page: 'athlete_package',
                  invoice_id: firstInvoiceId,
                  athlete_id: athleteId,
                  billing_start_date: dayjs(values.billingStartDate).format('YYYY-MM-DD'),
                });
              }
            }
            return;
          }

          successNotification('Athlete package creation success', `Athlete package successfully created.`);
          handleCloseAndReset();
        },
      });
    }
  };

  const handleCloseTapToPay = (): void => {
    setIsTapToPayModalOpened(false);
    setTapToPayInvoiceId(null);
    handleCloseAndReset();
    // Per spec: "Go back to athlete profile" — navigate after success or cancel
    if (athleteId) {
      navigate(`/users/athletes/${athleteId}`);
    }
  };

  const handleChangePaymentMethod = (): void => {
    // Mirror Retail's "Change Payment Method" semantics — keep the form values
    // (package, billing plan, dates, athlete) so the staff member just picks a
    // different payment method instead of re-entering everything.
    setIsTapToPayModalOpened(false);
    setTapToPayInvoiceId(null);
    form.setFieldValue('paymentMethod', '');
    form.setFieldValue('nonStripePaymentMethod', null);
    form.setFieldValue('isTapToPay', false);
  };

  const renderFollowUpBillingPlanPaymentDatesTable = (): JSX.Element => {
    const { successorBillingPlan } = form.values;
    if (successorBillingPlan && form.values.successorBillingStartDate) {
      const successorBillingWithInstallments = successorPackageData?.data.billingPlans.find(
        billingPlan => billingPlan.billingPlanId === successorBillingPlan,
      ) as BillingPlanWithInstallments;
      return renderBillingPlanPaymentDatesTable(
        form.values.successorBillingStartDate,
        successorBillingWithInstallments,
        form.values.successorIsProrated || false,
        successorPackageData?.data.invoiceTrigger || null,
      );
    }
    return <></>;
  };

  const handleSoldOutCheck = (values: FormValues): void => {
    if (
      packageData?.data.maxSalesAllowed &&
      Number(packageData?.data.numValidSales) >= packageData?.data.maxSalesAllowed
    ) {
      openSoldOutModal();
    } else {
      handleSubmit(values);
    }
  };

  const handleApplyDiscountToAllOptionShowCheck = (): boolean => {
    const { discount } = form.values;
    if (!discount) return false;
    const installmentsCount = form.values.billingPlan?.installments.length ?? 0;
    return installmentsCount > 1 && !discount.isOneTimeUse;
  };

  const handleDiscountChange = (discount: EligibleDiscountList | null): void => {
    form.setFieldValue('discount', discount);
    if (!discount || discount.isOneTimeUse) {
      form.setFieldValue('applyDiscountToAll', false);
    }
  };

  useEffect(() => {
    if (!packagesIsLoading && packagesData) {
      setPackagesTableData(packagesData.pages.map(page => page.data.results).flat());
    }
  }, [packagesIsLoading, packagesData]);

  // When the drawer opens in capture mode: reset stale state (from a prior session) and,
  // if we have a previously-captured config (e.g., user clicked Back from Reservation
  // Handling), hydrate the form from it.
  useEffect(() => {
    if (!isCaptureMode || !opened) return;
    form.reset();
    if (initialCaptured) {
      form.setValues({
        ...form.values,
        package: initialCaptured.package,
        billingPlan: initialCaptured.billingPlan,
        startDate: lockedStartDate ?? form.values.startDate,
        billingStartDate: initialCaptured.billingStartDate,
        athleteStatusOnStart: (initialCaptured.athleteStatusOnStart ??
          'No status change') as FormValues['athleteStatusOnStart'],
        athleteStatusOnEnd: (initialCaptured.athleteStatusOnEnd ??
          'No status change') as FormValues['athleteStatusOnEnd'],
        // handleCapture stores no paymentMethodId for a non-Stripe capture, and the select
        // holds that value itself, so without this the field re-opens blank.
        paymentMethod: initialCaptured.paymentMethodId ?? initialCaptured.nonStripePaymentMethod ?? '',
        nonStripePaymentMethod: initialCaptured.nonStripePaymentMethod,
        autoPay: initialCaptured.autoPay ? 'True' : 'False',
        autoRenew: initialCaptured.autoRenew ? 'True' : 'False',
        isProrated: initialCaptured.isProrated,
        includeFollowUpPackage: !!initialCaptured.followUpPackageId,
        successorPackage: initialCaptured.followUpPackageId ?? '',
        successorBillingPlan: initialCaptured.followUpBillingPlanId ?? '',
        discount: initialCaptured.discount,
        applyDiscountToAll: initialCaptured.applyDiscountToAll,
      });
    } else if (lockedStartDate) {
      form.setValues({
        ...form.values,
        startDate: lockedStartDate,
        billingStartDate: lockedStartDate,
        // Not redundant after form.reset(): form.values in this closure still holds the
        // pre-reset render's values, and spreading them here puts the old autoPay back.
        autoPay: defaultAutoPay,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, isCaptureMode]);

  useEffect(() => {
    if (!packageIsLoading && packageData && packageData.data.billingPlans) {
      const { attendanceRules, type, taxRate } = packageData.data;
      const totalSessions = computeTotalEstimatedSessions(attendanceRules, type);
      const taxRateValue = taxRate?.rate ?? 0;

      const billingPlansWithSessionValue = packageData.data.billingPlans.flat().map(bp => {
        let totalPrice = bp.totalPrice;
        if (isTaxInclusive && taxRateValue > 0) {
          totalPrice = Math.round(totalPrice / (1 + taxRateValue));
        }
        const sessionValue = computeSessionValue(totalPrice, totalSessions);
        return {
          ...bp,
          sessionValue: sessionValue !== null ? formatCurrency(sessionValue / 100) : '-',
        };
      });

      setBillingPlanTableData(billingPlansWithSessionValue);
    }
  }, [packageIsLoading, packageData, isTaxInclusive, formatCurrency]);

  useEffect(() => {
    if (isSuccessSuccessorPackage && successorPackageData && !isLoadingSuccessorPackage) {
      const billingPlans = successorPackageData.data.billingPlans.map(billingPlan => ({
        value: billingPlan.billingPlanId,
        label: billingPlan.name,
      }));
      setBillingPlanOptions(billingPlans);
      form.setFieldValue(
        'successorStatusOnStart',
        successorPackageData.data.athleteStatusOnStart || 'No status change',
      );
      form.setFieldValue(
        'successorStatusOnEnd',
        successorPackageData.data.athleteStatusOnEnd || 'No status change',
      );
      form.setFieldValue('includeFollowUpPackage', true);
      form.setFieldValue('successorIsProrated', successorPackageData.data.allowProration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successorPackageData, isSuccessSuccessorPackage, isLoadingSuccessorPackage]);

  useEffect(() => {
    if (packageIsSuccess && packageData && packageData.data.attendanceRules) {
      setDefaultSuccessorDates(form.values.startDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageData?.data.attendanceRules]);

  useEffect(() => {
    if (packageIsSuccess && packageData && !packageIsLoading) {
      form.setFieldValue('isProrated', packageData?.data.allowProration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageData?.data.allowProration, packageIsSuccess, packageIsLoading]);

  return (
    <Drawer
      opened={opened}
      onClose={handleCloseAndReset}
      size={1200}
      title={title}
      closeOnClickOutside
      footer={
        <Group position='right'>
          <Button variant='subtle' onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            data-cy='saveAthletePackageButton'
            loading={createMutation.isLoading || packagesIsLoading}
            onClick={() => (isCaptureMode ? handleSubmit(form.values) : handleSoldOutCheck(form.values))}
          >
            {!isCaptureMode &&
            form.values.isTapToPay &&
            !dayjs(form.values.billingStartDate).isAfter(dayjs(), 'day')
              ? 'Charge'
              : submitButtonLabel ?? 'Save'}
          </Button>
        </Group>
      }
    >
      <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing='xs' w='100%'>
          <Title order={4}>Package Selection</Title>
          <Text fz='sm' c='dimmed'>
            Select Package and Billing Plan
          </Text>
          <div className={classes.select}>
            <div className={classes.select}>
              {isDesktop ? (
                <>
                  <TextInput
                    icon={<IconSearch size={theme.fontSizes.md} />}
                    placeholder='Search Package'
                    onChange={handleSearchChange}
                    data-cy='searchAthletePackageInput'
                  />
                  <EventTypeMultiSelectAutoComplete
                    handleChange={handleEventTypeChange}
                    values={eventTypes}
                  />
                </>
              ) : (
                <>
                  <Select
                    data={[]}
                    searchValue={form.values.package?.name}
                    onClick={openPackageDrawer}
                    placeholder='Select Package'
                  />
                  <Select
                    data={[]}
                    searchValue={form.values.billingPlan?.name}
                    onClick={openBillingDrawer}
                    disabled={form.values.package === null}
                    placeholder='Select Billing Plan'
                  />
                </>
              )}
            </div>
            <div />
          </div>

          {isDesktop && (
            <Group align='top' grow data-cy='billingAndPackagesTable'>
              <MantineReactTable table={packagesTable} />
              {billingPlanTable}
            </Group>
          )}
          <Group align='top' grow>
            {form.errors.package ? (
              <Text color='red' size='sm' fs='italic'>
                {form.errors.package}
              </Text>
            ) : (
              <div />
            )}
            {form.errors.billingPlan ? (
              <Text color='red' size='sm' fs='italic'>
                {form.errors.billingPlan}
              </Text>
            ) : (
              <div />
            )}
          </Group>

          <Divider mx='-xl' mb='xs' />

          <div className={classes.detailsContainer}>
            <div className={classes.detailsInner}>
              <Title order={4}>Package Details</Title>
              {isCaptureMode && (
                <Text size='xs' c='dimmed'>
                  Locked by timing choice
                </Text>
              )}
              <Group grow align='top'>
                <DatePickerInput
                  data-cy='packageStartDate'
                  label='Package Start Date'
                  size='sm'
                  locale='us'
                  required
                  disabled={isCaptureMode || form.values.package?.startDate !== null}
                  {...form.getInputProps('startDate')}
                  onChange={handleStartDateChange}
                />
                <DatePickerInput
                  data-cy='billingStartDate'
                  label='Billing Start Date'
                  size='sm'
                  locale='us'
                  required
                  disabled={
                    form.values.package?.startDate !== null || form.values.isProrated || isCreditBasedPackage
                  }
                  {...form.getInputProps('billingStartDate')}
                />
              </Group>
              {packageData?.data.allowProration && (
                <Checkbox
                  label='Pro-rate first month credits and invoice and bill on the 1st of the month in the future'
                  {...form.getInputProps('isProrated')}
                  defaultChecked={packageData?.data.allowProration}
                  onChange={handleProrationChange}
                  disabled={!packageData?.data.allowProration}
                />
              )}
              {form.values.startDate?.toString() !== form.values.billingStartDate?.toString() && (
                <Group position='center'>
                  <Text color='red' size='sm' fs='italic' data-cy='errorCheckPackageStartDate'>
                    Billing start date differs from package start date and will remain offset for future
                    invoices.
                  </Text>
                </Group>
              )}
              {form.values.package?.invoiceTrigger === 'immediately' && (
                <Group position='center'>
                  <Text color='red' size='sm' fs='italic' data-cy='errorCheckPackageStartDate'>
                    The package template requires the first installment to be invoiced immediately
                  </Text>
                </Group>
              )}
              <Group grow align='top'>
                <AthleteStatusSelect
                  {...form.getInputProps('athleteStatusOnStart')}
                  label='Athlete Status Upon Start'
                  size='sm'
                  type='onStart'
                />
                <AthleteStatusSelect
                  {...form.getInputProps('athleteStatusOnEnd')}
                  label='Athlete Status Upon End'
                  size='sm'
                  type='onEnd'
                  excludeValue={form.values.athleteStatusOnStart}
                />
              </Group>
              <Group grow align='top'>
                <PaymentMethodSelect
                  {...form.getInputProps('paymentMethod')}
                  label='Payment Method'
                  size='sm'
                  placeholder='Select a payment method'
                  required
                  allowCash
                  allowTapToPay={!isCaptureMode && !!gymData?.data.stripeTerminalReader}
                  athleteId={athleteId}
                  // A restored capture already carries the member's chosen card, and the
                  // auto-select fires a commit after the effect that restores it, so on a
                  // re-open it would replace both that card and the auto-pay decided from it.
                  useDefaultPayment={!isCaptureMode || !initialCaptured}
                  data-cy='selectPaymentAthletePackage'
                  value={form.values.paymentMethod}
                  onChange={(value: string) => handlePaymentChange(value)}
                />
                <Select
                  label='AutoPay'
                  size='sm'
                  required
                  data-cy='autoPaySelection'
                  data={[
                    { value: 'True', label: 'On' },
                    { value: 'False', label: 'Off' },
                  ]}
                  // Tap to Pay always saves the card; spec requires autoPay locked on
                  // (esp. for multi-installment plans).
                  disabled={form.values.nonStripePaymentMethod !== null || form.values.isTapToPay}
                  {...form.getInputProps('autoPay')}
                />
              </Group>
              {form.values.isTapToPay && (
                <>
                  <StripeTerminalReaderStatusIndicator enabled={!!gymData?.data.stripeTerminalReader} />
                  <Group spacing='xs'>
                    <IconExclamationCircle size='1.2rem' />
                    <Text size='sm'>Card will be saved</Text>
                  </Group>
                </>
              )}
              <SimpleGrid cols={2}>
                <Select
                  label='AutoRenew'
                  size='sm'
                  required
                  disabled={
                    !!form.values.package?.isDelayed ||
                    !!form.values.package?.maxSalesAllowed ||
                    !!form.values.package?.oneTimePurchase ||
                    (allowsFollowUpPackage && form.values.includeFollowUpPackage)
                  }
                  data={[
                    { value: 'True', label: 'On' },
                    { value: 'False', label: 'Off' },
                  ]}
                  {...form.getInputProps('autoRenew')}
                />
                {allowsFollowUpPackage && (
                  <Checkbox
                    sx={{ paddingTop: '27px' }}
                    label='Include Follow-up Package'
                    size='sm'
                    radius={10}
                    checked={form.values.includeFollowUpPackage}
                    {...form.getInputProps('includeFollowUpPackage')}
                    onChange={event => handleIncludeFollowUpChange(event.currentTarget.checked)}
                  />
                )}
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <DiscountsSelect
                  athleteId={athleteId}
                  productId={form.values.package?.product}
                  label={
                    <Group spacing='xs'>
                      Apply Discounts
                      <KiloTooltip
                        wrapperDivClassVariantType='default'
                        label={TOOLTIP_MESSAGES.APPLY_DISCOUNTS}
                        withArrow
                        position='right-start'
                      >
                        <KiloActionIcon rootVariantType='default' variant='subtle'>
                          <IconInfoSquareRounded size='1.4rem' />
                        </KiloActionIcon>
                      </KiloTooltip>
                    </Group>
                  }
                  placeholder='Select a discount'
                  value={form.values.discount}
                  onChange={handleDiscountChange}
                />
                {handleApplyDiscountToAllOptionShowCheck() && (
                  <Checkbox
                    sx={{ paddingTop: '27px' }}
                    label='Apply to all'
                    size='sm'
                    radius={10}
                    checked={form.values.applyDiscountToAll}
                    {...form.getInputProps('applyDiscountToAll')}
                    onChange={event => form.setFieldValue('applyDiscountToAll', event.currentTarget.checked)}
                  />
                )}
              </SimpleGrid>
            </div>
            {form.values.package && form.values.billingPlan && form.values.billingStartDate ? (
              <Paper withBorder shadow='sm' p='md' className={classes.paper}>
                <Stack spacing='xs'>
                  <Group>
                    <Text size='sm' weight='bold' data-cy='packageAttachedToBPSelected'>
                      {form.values.package.name}
                    </Text>
                    <Text size='sm' data-cy='confirmBPSelected'>
                      {form.values.billingPlan.name}
                    </Text>
                  </Group>
                  <Text size='sm' color='dimmed'>
                    {copy.components.AthletePackageCreateForm.invoicesSummaryTitle}
                  </Text>
                  {form.values.discount && (
                    <SimpleGrid cols={2} w={'100%'} spacing={'space-between'}>
                      <Group align='center' spacing='xs'>
                        <IconTag size='1.4rem' color='#4AC360' />
                        <Text size='sm' color='green' weight={600}>
                          Discount: {form.values.discount.name}
                        </Text>
                      </Group>
                      <Text size='sm' color='green' align='end'>
                        {formatDiscountValue(form.values.discount, formatCurrency, false)}
                      </Text>
                    </SimpleGrid>
                  )}
                  {renderBillingPlanPaymentDatesTable(
                    form.values.billingStartDate,
                    form.values.billingPlan,
                    form.values.isProrated,
                    packageData?.data.invoiceTrigger || null,
                    form.values.discount,
                    form.values.applyDiscountToAll,
                  )}
                </Stack>
              </Paper>
            ) : (
              <div />
            )}
          </div>
          <Divider mx='-xl' mb='xs' />
          {allowsFollowUpPackage && form.values.includeFollowUpPackage && (
            <div className={classes.detailsContainer}>
              <div className={classes.detailsInner}>
                <Title order={4}>Follow-up Package Details</Title>
                <Group grow align='top'>
                  <PackageSelect
                    {...form.getInputProps('successorPackage')}
                    packageId={form.values.package?.packageId}
                    onChange={handleSuccessorPackageChange}
                    label='Follow-up Package'
                    size='sm'
                    required
                    placeholder='Select a follow-up package'
                    excludeCreditBased
                  />
                  <Select
                    {...form.getInputProps('successorBillingPlan')}
                    data={billingPlanOptions}
                    size='sm'
                    label='Follow-up Billing Plan'
                    placeholder='Select billing plan'
                    clearable
                    searchable
                    disabled={!form.values.successorPackage}
                    required
                  />
                </Group>
                {successorPackageData?.data.allowProration && (
                  <Checkbox
                    label='Pro-rate first month credits and invoice and bill on the 1st of the month in the future'
                    {...form.getInputProps('successorIsProrated')}
                    defaultChecked={successorPackageData?.data.allowProration}
                    onChange={handleSuccessorProrationChange}
                    disabled={!successorPackageData?.data.allowProration}
                  />
                )}
                <Group grow align='top'>
                  <DatePickerInput
                    label='Package Start Date'
                    size='sm'
                    locale='us'
                    required
                    minDate={new Date()}
                    {...form.getInputProps('successorStartDate')}
                    onChange={handleSuccessorStartDateChange}
                  />
                  <DatePickerInput
                    label='Billing Start Date'
                    size='sm'
                    locale='us'
                    required
                    minDate={new Date()}
                    disabled={form.values?.successorIsProrated || false}
                    {...form.getInputProps('successorBillingStartDate')}
                  />
                </Group>
                <Group grow align='top'>
                  <AthleteStatusSelect
                    {...form.getInputProps('successorStatusOnStart')}
                    label='Athlete Status Upon Start'
                    size='sm'
                    type='onStart'
                  />
                  <AthleteStatusSelect
                    {...form.getInputProps('successorStatusOnEnd')}
                    label='Athlete Status Upon End'
                    size='sm'
                    type='onEnd'
                  />
                </Group>
              </div>
              {form.values.successorPackage &&
              form.values.successorBillingPlan &&
              form.values.successorBillingStartDate ? (
                <Paper withBorder shadow='sm' p='md' className={classes.paper}>
                  <Stack spacing='xs'>
                    <Group>
                      <Text size='sm' weight='bold' data-cy='packageAttachedToBPSelected'>
                        {successorPackageData?.data.name}
                      </Text>
                      <Text size='sm' data-cy='confirmBPSelected'>
                        {
                          successorPackageData?.data.billingPlans.find(
                            billingPlan => billingPlan.billingPlanId === form.values.successorBillingPlan,
                          )?.name
                        }
                      </Text>
                    </Group>
                    <Text size='sm' color='dimmed'>
                      {copy.components.AthletePackageCreateForm.invoicesSummaryTitle}
                    </Text>
                    {renderFollowUpBillingPlanPaymentDatesTable()}
                  </Stack>
                </Paper>
              ) : (
                <div />
              )}
            </div>
          )}
        </Stack>
        <Drawer opened={isPackageDrawerOpen} onClose={closePackageDrawer} position='bottom'>
          <div className={classes.selectInDrawer}>
            <TextInput
              icon={<IconSearch size={theme.fontSizes.xs} />}
              placeholder='Search Package'
              onChange={handleSearchChange}
              value={search}
              sx={{ fontSize: theme.fontSizes.xs }}
            />
            <EventTypeMultiSelectAutoComplete
              handleChange={handleEventTypeChange}
              values={eventTypes}
              placeholder='Event Type'
            />
          </div>
          <MantineReactTable table={packagesTable} />
        </Drawer>
        <Drawer opened={isBillingDrawerOpen} onClose={closeBillingDrawer} position='bottom'>
          {billingPlanTable}
        </Drawer>
      </form>
      <Modal
        opened={isSoldOutModalOpen}
        onClose={closeSoldOutModal}
        title={<Text fw={700}>Sold Out</Text>}
        centered
      >
        <Text>
          This package has reached its quantity limit. Are you sure you want to assign this package to the
          athlete?
        </Text>
        <Divider my='md' />
        <Group position='right'>
          <Button variant='subtle' onClick={closeSoldOutModal}>
            Cancel
          </Button>
          <Button
            onClick={() => handleSubmit(form.values)}
            loading={createMutation.isLoading || packagesIsLoading}
          >
            Confirm
          </Button>
        </Group>
      </Modal>

      {tapToPayInvoiceId && athleteId && (
        <TapToPayModal
          opened={isTapToPayModalOpened}
          invoiceId={tapToPayInvoiceId}
          isChargeNow={tapToPayIsChargeNow}
          onClose={handleCloseTapToPay}
          onChangePaymentMethod={handleChangePaymentMethod}
          successButtonLabel='Go back to athlete profile'
          trackingContext={{
            page: 'athlete_package',
            athleteId,
            amount: form.values.billingPlan?.installments?.[0]?.price ?? 0,
            itemCount: 1,
            // Package Assignment always saves the card; future installments
            // auto-charge against it.
            saveCard: true,
            billingStartDate: dayjs(form.values.billingStartDate).format('YYYY-MM-DD'),
          }}
        />
      )}
    </Drawer>
  );
};

export default AthletePackageCreateForm;
